import React from 'react';
import { Button } from '../ui/button';
import { Clipboard, Bot, Brain, Zap, X } from 'lucide-react';

export interface UsePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  promptTitle: string;
  promptText: string;
  onUseWithAgent: (agent: 'claude' | 'copilot' | 'chatgpt' | 'clipboard') => void;
}

export const UsePromptDialog: React.FC<UsePromptDialogProps> = ({
  isOpen,
  onClose,
  promptTitle,
  promptText,
  onUseWithAgent
}) => {
  if (!isOpen) return null;

  const agents = [
    {
      id: 'claude' as const,
      name: 'Claude',
      url: 'https://claude.ai',
      icon: Brain,
      description: 'Use with Claude AI',
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      id: 'copilot' as const,
      name: 'Microsoft Copilot',
      url: 'https://copilot.cloud.microsoft',
      icon: Bot,
      description: 'Use with Microsoft Copilot',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'chatgpt' as const,
      name: 'ChatGPT',
      url: 'https://chat.openai.com',
      icon: Zap,
      description: 'Use with ChatGPT',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      id: 'clipboard' as const,
      name: 'Copy to Clipboard',
      url: '',
      icon: Clipboard,
      description: 'Copy prompt to clipboard only',
      color: 'bg-gray-500 hover:bg-gray-600'
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold">
            Use Prompt: {promptTitle}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="p-6">
          <p className="text-sm text-muted-foreground mb-6">
            Choose how you'd like to use this prompt:
          </p>
          
          <div className="grid gap-3">
            {agents.map((agent) => {
              const IconComponent = agent.icon;
              return (
                <Button
                  key={agent.id}
                  variant="outline"
                  className={`${agent.color} text-white border-0 h-auto p-4 justify-start gap-3 transition-colors w-full`}
                  onClick={() => onUseWithAgent(agent.id)}
                >
                  <IconComponent className="w-5 h-5 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">{agent.name}</div>
                    {agent.url && (
                      <div className="text-xs opacity-90">{agent.url}</div>
                    )}
                  </div>
                </Button>
              );
            })}
          </div>
          
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Preview:</strong> {promptText.slice(0, 100)}
              {promptText.length > 100 ? '...' : ''}
            </p>
          </div>
          
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};